import { useCallback, useMemo, useRef } from 'react';
import { Box } from '@/UI/Box/Box';
import { color, EL_COLORS } from '@/colors';
import { Flex, FlexCol } from '@/UI/styles';
import { useCaptureElementWheelScroll } from '@/hooks/useCaptureElementWheelScroll';
import { useIsHovered } from '@/hooks/useIsHovered';
import { useIsNodeDiagonalResizeEnabled } from '@/hooks/useIsNodeDiagonalResizeEnabled';
import { useResizeScalerAttributes } from '@/hooks/useNodeResizeStyles';
import { GRID_SCROLL_DEFAULT_MAX_HEIGHT } from '@/consts/node-ui.consts';
import { useFlowView } from '@/components/Recipe/FlowContext';
import { MediaToCanvasDragSource } from '@/hooks/useMediaToCanvasDrag';
import { useMediaToCanvasGridDrag } from '@/hooks/useMediaToCanvasGridDrag';
import { getMediaDragPreviewUrl } from '@/utils/mediaToCanvasDrag.utils';
import { useResolvedAssets } from '@/state/workflow.state';
import { useGridViewerPendingCount } from './GridViewerPendingContext';
import { GridItem } from './GridItem';
import type { NodeId, FileKind } from 'web';
import type { IndexedUploadedAsset } from '../Common/ImageList/ImageList';

const NODE_WIDTH = 422;

/**
 * Assets whose viewer is `width: 100%` plus an `aspectRatio`, so the column already decides its
 * height. Handing one a pixel height instead crops it, since that height comes from the fixed
 * {@link NODE_WIDTH} basis and doesn't grow when the user widens the node.
 */
const RATIO_SIZED_ASSET_TYPES = new Set<string>(['image', 'video']);

/** 3D viewers are ratio-sized too, but only once nodes can be widened — see the flag-off set above. */
const RATIO_SIZED_ASSET_TYPES_WITH_3D = new Set<string>([...RATIO_SIZED_ASSET_TYPES, '3D']);

const getItemSquareSize = (columnCount: number, hasDynamicItemHeight: boolean): number | undefined => {
  const columnWidth = NODE_WIDTH / columnCount;
  return !hasDynamicItemHeight ? columnWidth : undefined;
};

type IndextFileKind = FileKind & { globalIndex: number };
type IndexedFile = IndexedUploadedAsset | IndextFileKind;

type ColumnItem = IndexedFile | null;

function GridItemPlaceholder({ aspectRatio }: { aspectRatio: number }) {
  return (
    <Box
      className="media-container-dark"
      sx={{
        borderRadius: 2.5,
        width: '100%',
        aspectRatio,
        border: `1px solid ${color.White04_T}`,
      }}
    />
  );
}

export interface GridViewerProps {
  nodeId: NodeId;
  files: IndexedFile[];
  selectedIndex: number;
  onFileSelect: (index: number) => void;
  className?: string;
  /**
   * The node was resized, so the height it was dragged to is the grid's ceiling: drop the fixed cap
   * and let the grid hug its tiles up to that, scrolling beyond it. Both rely on the ancestor height,
   * so callers must render this inside a flex column. Pass `false` for a node nobody resized — the cap
   * then keeps its height constant however many files arrive.
   */
  fillHeight?: boolean;
  onContextMenu?: (event: React.MouseEvent, index: number) => void;
  onTextChange?: (index: number, newValue: string) => void;
  /**
   * Node-level gate only. Import/Media iterator pass `editable`; model node grid uses editor role + `!locked`
   * and is off while the expanded media gallery is open (see ModelResults).
   */
  mediaToCanvasDragEnabled?: boolean;
  /** Payload `source` for drag payload; default `import`. */
  mediaToCanvasDragSource?: MediaToCanvasDragSource;
}

export const GridViewer = ({
  nodeId,
  files,
  selectedIndex,
  onFileSelect,
  className,
  fillHeight = false,
  onContextMenu,
  onTextChange,
  mediaToCanvasDragEnabled = false,
  mediaToCanvasDragSource = MediaToCanvasDragSource.Import,
}: GridViewerProps) => {
  const pendingCount = useGridViewerPendingCount();
  const resolvedAssets = useResolvedAssets();
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const flowView = useFlowView();
  const isDiagonalResizeEnabled = useIsNodeDiagonalResizeEnabled();
  const totalItemCount = files.length + pendingCount;
  const isSingleFile = totalItemCount === 1;

  const getAssetKindAtIndex = useCallback((index: number) => files.find((f) => f.globalIndex === index)?.type, [files]);

  const getPreviewUrlAtIndex = useCallback(
    (index: number) => {
      const file = files.find((f) => f.globalIndex === index);
      return file ? getMediaDragPreviewUrl(file, mediaToCanvasDragSource, resolvedAssets) : null;
    },
    [files, mediaToCanvasDragSource, resolvedAssets],
  );

  const {
    isGridActive: isMediaDragToCanvasGridActive,
    onGridDragStart: handleMediaToCanvasGridDragStart,
    onDragModifier: handleMediaToCanvasDragModifier,
    onDragEnd: handleMediaToCanvasDragEnd,
    isSourceDimmed,
    isCanvasDragSessionActive,
  } = useMediaToCanvasGridDrag({
    source: mediaToCanvasDragSource,
    sourceNodeId: nodeId,
    enabled: Boolean(mediaToCanvasDragEnabled),
    fileCount: files.length,
    getAssetKindAtIndex,
    getPreviewUrlAtIndex,
  });

  const onMediaToCanvasDragEnd = useCallback(() => {
    handleMediaToCanvasDragEnd();
    void flowView.loadResources();
  }, [flowView, handleMediaToCanvasDragEnd]);

  const distributeItemsIntoColumns = useCallback((): ColumnItem[][] => {
    const total = files.length + pendingCount;
    const columns = total === 1 ? 1 : total <= 4 ? 2 : 3;
    const cols: ColumnItem[][] = Array.from({ length: columns }, () => []);
    files.forEach((file, i) => cols[i % columns].push(file));
    for (let i = 0; i < pendingCount; i++) cols[(files.length + i) % columns].push(null);
    return cols;
  }, [files, pendingCount]);

  const ratioSizedTypes = isDiagonalResizeEnabled ? RATIO_SIZED_ASSET_TYPES_WITH_3D : RATIO_SIZED_ASSET_TYPES;
  const hasDynamicItemHeight = Boolean(files?.[0] && ratioSizedTypes.has(files[0].type));
  const isTextItem = files && files[0]?.type === 'text';
  const fillItemHeight = isSingleFile && (fillHeight || isTextItem);
  const gridResizeScaler = isDiagonalResizeEnabled && !isSingleFile;
  const resizeScalerAttributes = useResizeScalerAttributes(!isSingleFile);
  const gridColumns = useMemo(distributeItemsIntoColumns, [distributeItemsIntoColumns]);

  const fixedItemSquareSize = useMemo(() => {
    if (fillItemHeight) return undefined;
    return getItemSquareSize(gridColumns.length, hasDynamicItemHeight);
  }, [gridColumns.length, hasDynamicItemHeight, fillItemHeight]);

  const lastKnownAspectRatioRef = useRef(1);
  const file = files && (files[0] as { width?: number; height?: number } | undefined);
  if (file?.width && file?.height) {
    lastKnownAspectRatioRef.current = file.width / file.height;
  }
  const placeholderAspectRatio = lastKnownAspectRatioRef.current;

  const { isHovered, onMouseEnter, onMouseLeave } = useIsHovered();
  useCaptureElementWheelScroll(isHovered ? scrollContainerRef : { current: null });

  return (
    <FlexCol className={className} sx={{ width: '100%', ...(fillHeight && { flex: 1, minHeight: 0 }) }}>
      <Box
        ref={scrollContainerRef}
        data-testid={`grid-viewer-scroll-${nodeId}`}
        {...resizeScalerAttributes}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        sx={{
          // Resized: fill the height the node was given. Otherwise cap it — the node hugs the grid,
          // so without a cap the file count would decide the node's height.
          ...(fillHeight && { flex: 1, minHeight: 0 }),
          ...(gridResizeScaler && { scrollbarGutter: 'stable' }),
          ...(!fillHeight && !isSingleFile && { maxHeight: GRID_SCROLL_DEFAULT_MAX_HEIGHT }),
          ...(fillItemHeight && { display: 'flex', flexDirection: 'column' }),
          overflow: fillItemHeight ? 'hidden' : 'auto',
          border: `1px solid ${EL_COLORS.BoxBorder}`,
          p: 0.25,
          pr: 0.25,
          borderRadius: 2,
        }}
      >
        <Flex
          sx={{
            gap: 0.5,
            ...(fillItemHeight && { flex: 1, minHeight: 0, alignItems: 'stretch' }),
          }}
        >
          {gridColumns.map((columnItems, columnIndex) => (
            <FlexCol key={`column-${columnIndex}`} sx={{ flex: 1, minWidth: 0, gap: 0.5 }}>
              {columnItems.map((item, i) =>
                item !== null ? (
                  <GridItem
                    key={`file-${item.globalIndex}`}
                    nodeId={nodeId}
                    file={item}
                    index={item.globalIndex}
                    isSelected={item.globalIndex === selectedIndex}
                    onFileClick={onFileSelect}
                    fillHeight={fillItemHeight}
                    onContextMenu={onContextMenu}
                    onTextChange={onTextChange}
                    fixedItemSize={fixedItemSquareSize}
                    mediaToCanvasDragActive={isMediaDragToCanvasGridActive}
                    onMediaToCanvasDragStart={handleMediaToCanvasGridDragStart}
                    mediaToCanvasDragSourceDimmed={isSourceDimmed(item.globalIndex)}
                    onMediaToCanvasDragModifier={handleMediaToCanvasDragModifier}
                    onMediaToCanvasDragEnd={onMediaToCanvasDragEnd}
                    suspendMediaPlayback={isCanvasDragSessionActive}
                  />
                ) : (
                  <GridItemPlaceholder key={`placeholder-${i}`} aspectRatio={placeholderAspectRatio} />
                ),
              )}
            </FlexCol>
          ))}
        </Flex>
      </Box>
    </FlexCol>
  );
};
